import React from 'react';
import { Search, X, Calculator, Droplets, Percent, Coins, ArrowLeftRight } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';

// Reusing the CalculatorLogo component locally or importing it if better located
const CalculatorLogo: React.FC<{ size?: 'xs' | 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => {
    const dimensions = {
        xs: {
            container: 'h-6 w-6 rounded-md',
            screen: 'h-1',
            screenTop: 'top-0.5 left-0.5 right-0.5',
            buttonsContainer: 'space-x-0.5',
            button: 'h-0.5 w-0.5',
            iconSize: 10,
        },
        sm: {
            container: 'h-8 w-8 rounded-md',
            screen: 'h-1.5',
            screenTop: 'top-0.5 left-0.5 right-0.5',
            buttonsContainer: 'space-x-0.5',
            button: 'h-1 w-1',
            iconSize: 12,
        },
        md: {
            container: 'h-12 w-12 rounded-lg',
            screen: 'h-2',
            screenTop: 'top-1 left-1 right-1',
            buttonsContainer: 'space-x-1',
            button: 'h-1.5 w-1.5',
            iconSize: 16,
        },
        lg: {
            container: 'h-20 w-20 rounded-xl',
            screen: 'h-3',
            screenTop: 'top-1.5 left-1.5 right-1.5',
            buttonsContainer: 'space-x-1.5',
            button: 'h-2 w-2',
            display: 'h-3 w-10',
            grid: 'grid-cols-4 gap-1',
            gridButton: 'h-1.5 w-1.5',
            iconSize: 24,
        }
    };

    const d = dimensions[size];

    return (
        <div className={`relative ${d.container} bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg`}>
            <div className={`absolute ${d.screenTop} ${d.screen} bg-cyan-300/30 rounded-t-sm`}></div>
            <div className="flex flex-col items-center">
                <div className={`flex ${d.buttonsContainer} mb-0.5`}>
                    <div className={`${d.button} bg-white rounded-full`}></div>
                    <div className={`${d.button} bg-white rounded-full`}></div>
                    <div className={`${d.button} bg-white rounded-full`}></div>
                    {size === 'lg' && <div className={`${d.button} bg-white rounded-full`}></div>}
                </div>

                {size === 'lg' && (
                    <>
                        <div className="h-3 w-10 bg-cyan-200/90 rounded-sm mb-2.5 flex items-center justify-end px-1">
                            <div className="text-[8px] font-mono text-blue-800">123.45</div>
                        </div>
                        <div className="grid grid-cols-4 gap-1">
                            {Array.from({ length: 12 }).map((_, i) => (
                                <div key={i} className="h-1.5 w-1.5 bg-white/70 rounded-full"></div>
                            ))}
                        </div>
                    </>
                )}
                <Calculator size={d.iconSize} className="text-white mt-1" />
            </div>
        </div>
    );
};

interface HeroSectionProps {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    searchResults: Array<{
        id: string;
        name: string;
        icon: React.ReactNode;
        description?: string;
    }>;
    onNavigate: (id: string) => void;
    getUrlFromId: (id: string) => string;
}

const HeroSection: React.FC<HeroSectionProps> = ({
    searchTerm,
    setSearchTerm,
    searchResults,
    onNavigate,
}) => {
    const { t, getLink, language } = useLanguage();

    // Helper for links
    // Helper for links
    const CalculatorLink = ({ id, children, className }: { id: string, children: React.ReactNode, className?: string }) => {
        const href = getLink(id);
        const isExternal = href.startsWith('http');
        return (
            <a
                href={href}
                onClick={(e) => {
                    if (isExternal) return;
                    e.preventDefault();
                    onNavigate(id);
                }}
                className={className}
            >
                {children}
            </a>
        );
    };

    return (
        <div className="px-4 md:px-8 mx-auto mb-12 text-center">
            <div className="flex justify-center items-center mb-4">
                <a href={getLink('home')} className="flex items-center">
                    <div className="hidden sm:block mr-3">
                        <CalculatorLogo size="md" />
                    </div>
                    <div className="bg-gradient-to-r from-blue-500 to-teal-400 bg-clip-text text-transparent 
                        text-3xl md:text-5xl font-bold">
                        {t('home.heroTitle')}
                    </div>
                </a>
            </div>

            <h2 className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
                {t('home.heroSubtitle')}
            </h2>

            {/* Büyük Arama Kutusu */}
            <div className="max-w-2xl mx-auto mb-8">
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                        <Search size={20} className="text-cyan-500" />
                    </div>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('home.searchPlaceholder')}
                        className="w-full py-4 pl-14 pr-12 bg-[var(--card-bg)] border border-[var(--border-color)] 
                     rounded-lg text-md focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-lg text-[var(--text-color)]"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute inset-y-0 right-0 pr-5 flex items-center text-gray-400 hover:text-gray-500"
                        >
                            <X size={20} />
                        </button>
                    )}
                </div>

                {/* Arama Sonuçları */}
                {searchTerm && searchResults.length > 0 && (
                    <div className="mt-2 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-lg shadow-lg 
                         max-h-96 overflow-y-auto text-left z-50 relative">
                        <div className="p-2">
                            <h3 className="text-xs uppercase text-cyan-500/70 font-semibold px-2 pt-1 pb-2 border-b border-[var(--border-color)]">
                                {t('home.searchResults')} ({searchResults.length})
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 pt-2">
                                {searchResults.map((calc) => (
                                    <CalculatorLink
                                        key={calc.id}
                                        id={calc.id}
                                        className="flex items-center w-full text-left px-3 py-3 rounded-md 
                               hover:bg-[var(--hover-bg)] transition-colors"
                                    >
                                        <span className="mr-3 text-cyan-400 flex-shrink-0">
                                            {calc.icon}
                                        </span>
                                        <div>
                                            <div className="font-medium text-[var(--text-color)]">{calc.name}</div>
                                            {calc.description && (
                                                <div className="text-xs text-gray-400 line-clamp-1 mt-0.5">{calc.description}</div>
                                            )}
                                        </div>
                                    </CalculatorLink>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Arama Sonucu Bulunamadı */}
                {searchTerm && searchResults.length === 0 && (
                    <div className="mt-2 bg-[var(--card-bg)] border border-[var(--border-color)] rounded-lg shadow-lg p-6 text-center">
                        <div className="text-gray-400">
                            "<span className="text-cyan-400 font-medium">{searchTerm}</span>" {t('home.noResults')}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                            {t('home.tryDifferent')}
                        </div>
                    </div>
                )}
            </div>

            <div className="max-w-md mx-auto">
                {!searchTerm && (
                    <div className="flex flex-col md:flex-row justify-center gap-4">
                        <CalculatorLink
                            id={language === 'nl' ? 'percentage' : 'calcApp'}
                            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 
                            text-white font-medium py-3 px-6 rounded-lg transition-all duration-300
                            flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20"
                        >
                            {language === 'tr' ? <Calculator size={20} /> : <Percent size={20} />}
                            {language === 'tr' ? t('home.calculator') : t('tools.percentage')}
                        </CalculatorLink>

                        <CalculatorLink
                            id={language === 'nl' ? 'dollarEuro' : 'waterNeed'}
                            className="bg-[var(--hover-bg)] hover:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300 border border-[var(--border-color)]
                            font-medium py-3 px-6 rounded-lg transition-all duration-300
                            flex items-center justify-center gap-2"
                        >
                            {language === 'tr' ? <Droplets size={20} /> : <ArrowLeftRight size={20} />}
                            {language === 'tr' ? t('home.waterCalc') : t('tools.dollarEuro')}
                        </CalculatorLink>
                    </div>
                )}
            </div>

        </div>
    );
};

export default HeroSection;
